import { Injectable } from '@angular/core';
import { Http, Response, RequestOptions, Headers, URLSearchParams } from '@angular/http';
import { environment } from '../environments/environment';
// import { Observable } from 'rxjs/Observable';
import { Observable } from 'rxjs'
import { ApiService } from 'app/api.service';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';

@Injectable()
export class HubService {

  constructor(private http: Http, private apiService: ApiService) { }

  /**
  * ---------------------------------------------
  * Build a default request header based on current env
  * @param {string} - optional - user sessionToken
  * @returns {object} - formatted header object
  * ---------------------------------------------
  */
  private buildHeader(sessionToken = null) {
    let headers = new Headers({ 'Content-Type': 'application/json' });
    headers.append('X-Parse-Application-Id', environment.parse.appId);
    headers.append('X-Parse-Javascript-Key', environment.parse.jsKey);
    if (sessionToken) {
      headers.append('X-Parse-Session-Token', sessionToken);
    }
    return headers;
  }

  /**
  * ---------------------------------------------
  * Generic Hub API GET wrapper
  * e.g. this.hubService.getResource('RideSession', {'_id':'12345'}, 20, 0, null, null).subscribe(res => {}, err => {});
  *
  * @param {string} `resource` e.g. 'RideSession', 'Workout', 'Plan'
  * @param {object} `where`  Parse search object e.g. { "createdAt": { "$gt": { "__type": "Date", "iso": some-sio-date } } }
  * @param {number} `limit`
  * @param {number} `skip`
  * @param {string} `order` e.g. 'startDate' or '-startDate'
  * @param {array} `includes` e.g. ['_User', 'RideSessionSummary', 'RideSessionTraining']
  * @returns {array} an array of result objects as an Observable
  * ---------------------------------------------
  */
  public getResource(
    resource: string = null,
    where: object = null,
    limit: number = 500,
    skip: number = null,
    order: string = 'createdAt',
    includes: Array<any> = null,
    headers: any = null
  ): Observable<any> {

    // build the auth headers
    if(this.apiService.Parse.User.current()){
      headers = this.buildHeader(this.apiService.Parse.User.current().getSessionToken());
    }else{
      headers = this.buildHeader(false);
    }
    

    // build search params
    let params = new URLSearchParams();
    if (limit) { params.set('limit', limit.toString()); }
    if (skip) { params.set('skip', skip.toString()); }
    if (order) { params.set('order', order); }
    if (includes) {
      for (let include of includes) {
        params.append('include', include);
      }
    }
    if (where) { params.set('where', JSON.stringify(where)); }

    // combine any request options
    let options;
    if(headers){
      options = new RequestOptions({ headers: headers, search: params });
    }else{
      options = new RequestOptions({ search: params });
    }    

    // return the requst response
    return this.http.get(environment.parse.apiUrl + '/classes/' + resource, options)
      .map(res => res.json().results || {})
      .catch(this.errorHandler);
  }

  /**
  * ---------------------------------------------
  * Generic Hub API file GET wrapper
  *
  * @param {string} `file`
  * @returns {array} an array of result objects as an Observable
  * ---------------------------------------------
  */
  public getFile(
    file: string = null
  ): Observable<any> {

    // build the auth headers
    let headers = this.buildHeader(this.apiService.Parse.User.current().getSessionToken());

    // build search params
    let params = new URLSearchParams();

    // combine any request options
    let options = new RequestOptions({ headers: headers, search: params });

    // return the requst response
    return this.http.get(environment.parse.apiUrl + '/files/' + file, options)
      .map(res => res.json() || {})
      .catch(this.errorHandler);
  }

  /**
  * ---------------------------------------------
  * Generic Hub API PUT wrapper
  * e.g. this.hubService.putResource('Workout', objectId, workoutObject).subscribe(res => {}, err => {});
  *
  * @param {string} `resource` e.g. 'Workout'
  * @param {string} `objectId` id of the object to update, gets appended to the uri
  * @param {object} `payload`
  * @returns {array} an array of result objects as an Observable
  * ---------------------------------------------
  */
  public putResource(resource: string = null, objectId: string = null, payload: object = null): Observable<any> {

    // build the auth headers
    let headers = this.buildHeader(this.apiService.Parse.User.current().getSessionToken());

    // combine any request options
    let options = new RequestOptions({ headers: headers });

    // return the requst response
    return this.http.put(environment.parse.apiUrl + '/classes/' + resource + '/' + objectId, JSON.stringify(payload), options)
      .map(res => res.json().results || {})
      .catch(this.errorHandler);
  }

  /**
  * ---------------------------------------------
  * Generic Hub API POST wrapper
  * e.g. this.hubService.postResource('Workout', workoutObject).subscribe(res => {}, err => {});
  *
  * @param {string} `resource` e.g. 'Workout'
  * @param {object} `payload`
  * @returns {array} an array of result objects as an Observable
  * ---------------------------------------------
  */
  public postResource(resource: string = null, payload: object = null): Observable<any> {

    // build the auth headers
    let headers = this.buildHeader(this.apiService.Parse.User.current().getSessionToken());

    // combine any request options
    let options = new RequestOptions({ headers: headers });

    // return the requst response
    return this.http.post(environment.parse.apiUrl + '/' + resource, payload, options)
      .map(res => res.json() || {})
      .catch(this.errorHandler);
  }

  /**
  * ---------------------------------------------
  * Generic Hub API DELETE wrapper
  * e.g. this.hubService.postResource('Workout', workoutObject).subscribe(res => {}, err => {});
  *
  * @param {string} 'objectId'
  * @returns {object} the delete response
  * ---------------------------------------------
  */
  public deleteResource(resource: string = null, objectId: string = null): Observable<any> {

    if(!objectId){return;}

    // build the auth headers
    let headers = this.buildHeader(this.apiService.Parse.User.current().getSessionToken());

    // combine any request options
    let options = new RequestOptions({ headers: headers });

    // return the requst response
    return this.http.delete(environment.parse.apiUrl + '/classes/' + resource + '/' + objectId, options)
      .map(res => res.json().results || {})
      .catch(this.errorHandler);
  }

  /**
  * ---------------------------------------------
  * Handle and return formatted error
  * @param {Object} - the error
  * @returns {Object} - formatted error
  * ---------------------------------------------
  */
  private errorHandler(error: Response | any): Observable<any> {
    let errMsg: string;

    // build a generic error message to use if we dont define something specific
    if (error instanceof Response) {
      const body = error.json() || '';
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }

    // specific error messages
    if (error.status == 401){ // unauthorised
      errMsg = "You do not have permission to perform this action (401 Error)";
    }else if(error.status == 400){ // bad request
      errMsg = "You do not have permission to perform this action (400 Error)";
    }else if(error.status == 500){ // internal server error
      errMsg = "Internal Server error (400 Error)";
    }else if(error.status == 404){ // not found error
      errMsg = "Sorry, we couldn't find that record (404 Error)";
    }

    return Observable.throw(errMsg);
  }

}



// WEBPACK FOOTER //
// /tmp/build_769bc651/src/app/hub.service.ts